/**
 * RESPONSIBILITY: keep the document itself in step with the content —
 * the palette, the tab title, the theme-colour meta tag, the tab icon.
 *
 * These are side effects on `document`, not rendering, which is why they live apart
 * from the components. One `cms.get([...])` call, so it is still one request.
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { applyCssVars, noteEpoch, setEpochHandler, useFlag, FLAG_SECTION_OUTLINES }
  from '@framework/react';
import { SectionName } from '@app/sections';
import { cms } from './cms';
import { THEME_VARS } from './theme-vars';

export function useDocumentChrome(): void {
  const qc = useQueryClient();
  const outlines = useFlag(FLAG_SECTION_OUTLINES);

  // A flush anywhere bumps the server epoch; every other client notices on its next
  // revalidation and drops its cache too.
  useEffect(() => setEpochHandler((e) => noteEpoch(e, qc)), [qc]);

  const { theme, header } = cms.get([SectionName.Theme, SectionName.Header]);

  useEffect(() => {
    if (!theme.data) return;
    const data = theme.data as unknown as Record<string, unknown>;
    applyCssVars(THEME_VARS, data);
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', String(data.primary ?? ''));
  }, [theme.data]);

  useEffect(() => {
    if (header.data) document.title = header.data.fullName || header.data.mark;
  }, [header.data]);

  /*
   * The tab icon is the church's logo, for the same reason the title is its name: it is
   * content, and the shell deliberately ships none. Without this every page fetched
   * /favicon.ico, got a 404 and logged it — a broken request on every single visit.
   *
   * The <link> is created here rather than sitting empty in index.html, because an empty
   * href resolves to the page itself and browsers then download the document again.
   */
  useEffect(() => {
    const href = header.data?.logo?.path;
    if (!href) return;

    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
      ?? document.head.appendChild(Object.assign(document.createElement('link'), { rel: 'icon' }));
    link.href = href;
  }, [header.data]);

  useEffect(() => {
    document.body.classList.toggle('show-section-outlines', outlines);
  }, [outlines]);
}
